<template>
  <V2AtomsBaseCard
    :size="size"
    class="flex flex-col gap-2 md:gap-4 group h-full relative"
    :class="[component.wrapper, props.wrapperClass]"
  >
    <!-- eslint-disable vue/no-v-html -->
    <div
      v-if="$slots.header || headerIcon || imagePath || badgeTitle"
      class="header"
    >
      <slot name="header">
        <Component
          :is="headerIcon"
          v-if="headerIcon"
          :fill="getHeaderIconColor"
          v-bind="iconProps"
          :class="headerIconClass"
        />

        <img
          v-if="imagePath"
          :src="imagePath"
          class="rounded-2xl w-full h-full"
          alt="header-card"
        />

        <V2AtomsLabel
          v-if="badgeTitle"
          :theme="!inverted ? 'inverted' : 'default'"
        >
          {{ badgeTitle }}
        </V2AtomsLabel>
      </slot>
    </div>

    <div class="flex-1 flex flex-col gap-2 md:gap-4" :class="[`text-${textAlign}`]">
      <slot>
        <div
          v-if="title"
          class="font-semibold text-lg/[120%] md:text-2xl/[120%]"
          :class="[
            inverted
              ? 'text-semantic-inverse-on-surface'
              : 'text-semantic-surface-on-surface',
          ]"
          v-html="title"
        />

        <div
          v-if="description"
          class="md:text-lg/[150%]"
          :class="[
            inverted
              ? 'text-semantic-inverse-on-surface'
              : 'text-semantic-surface-on-surface',
          ]"
          v-html="description"
        />
      </slot>
    </div>

    <div v-if="hasCta || $slots.footer" class="footer">
      <slot name="footer">
        <div v-if="hasCta">
          <div class="group-hover:hidden">
            <V2AtomsButton
              has-icon-right
              theme="tertiary"
              :inverted="inverted"
              :to="ctaBtnTo"
              size="small"
              :target="ctaBtnTarget"
            >
              {{ ctaBtnLabel }}
            </V2AtomsButton>
          </div>

          <div class="hidden group-hover:block">
            <V2AtomsButton
              has-icon-right
              :inverted="inverted"
              size="small"
              :to="ctaBtnTo"
              :target="ctaBtnTarget"
            >
              {{ ctaBtnLabel }}
            </V2AtomsButton>
          </div>
        </div>
      </slot>
    </div>
  </V2AtomsBaseCard>
</template>

<script setup>
import V2IconsCheck from '~/components/v2/icons/Check.vue'

defineOptions({
  name: 'V2MoleculesCardFeatureCard'
})

const props = defineProps({
  size: {
    type: String,
    default: 'small',
    validator: value => ['small', 'base', 'large'].includes(value)
  },

  inverted: {
    type: Boolean,
    default: false
  },

  hasCta: {
    type: Boolean,
    default: false
  },

  textAlign: {
    type: String,
    default: 'center',
    validator: value => ['left', 'center', 'right'].includes(value)
  },

  title: {
    type: String,
    default: ''
  },

  description: {
    type: String,
    default: ''
  },

  wrapperClass: {
    type: String,
    default: ''
  },

  ctaBtnLabel: {
    type: String,
    default: 'This is button'
  },

  ctaBtnTo: {
    type: String,
    default: ''
  },

  ctaBtnTarget: {
    type: String,
    default: ''
  },

  badgeTitle: {
    type: String,
    default: ''
  },

  imagePath: {
    type: String,
    default: ''
  },

  headerIcon: {
    type: [String, Object],
    default: V2IconsCheck
  },

  headerIconClass: {
    type: String,
    default: ''
  },

  iconProps: {
    type: Object,
    default: () => ({})
  }
})

const component = computed(() => {
  const wrapper = (() => {
    let classes = ''

    if (props.hasCta) {
      classes += 'border-2 border-semantic-outline-variant'
    } else {
      classes += 'border-2 border-semantic-outline-variant'
    }

    if (props.hasCta) {
      classes += ' hover:border-semantic-primary-default'
    }

    if (props.inverted) {
      classes +=
        ' hover:border-semantic-inverse-primary !bg-semantic-inverse-surface'
    }

    classes +=
      ' hover:shadow-elevation-1 transition-all duration-300 ease-in-out'

    return classes
  })()

  return {
    wrapper
  }
})

const getHeaderIconColor = computed(() => {
  if (props.inverted) {
    return semanticColorLookup('inverse.primary-variant-light')
  }
  return semanticColorLookup('primary.default')
})
</script>
